import { AlertErrorCircle, Document, Template, XClear } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Tooltip } from '@mui/material'
import { clsx } from 'clsx'

type UserFilePillProps = {
  fileName: string
  canView: boolean
  isProcessing: boolean
  isError: boolean
  errorMessage?: string | null
  onView: () => void
  onRemove: () => void
  // Visual variant (presentational, no domain meaning): 'emphasized' is the darker, filled chip
  // with the template-style icon; 'default' is the standard user-file chip.
  variant?: 'default' | 'emphasized'
}

/**
 * A selected-file chip for the new user_files flow. Presentational only - the parent
 * resolves the file record (in one batched fetch) and passes its display state in as
 * primitives, so the pill never fetches.
 */
export default function UserFilePill({ fileName, canView, isProcessing, isError, errorMessage, onView, onRemove, variant = 'default' }: UserFilePillProps) {
  const isEmphasized = variant === 'emphasized'
  const icon = isError ? (
    <AlertErrorCircle className="w-4 h-4 text-red-700" />
  ) : isProcessing ? (
    <CircularProgressContinuousSized size={12} thickness={7} />
  ) : isEmphasized ? (
    <Template className="w-[18px] h-[18px] shrink-0" />
  ) : (
    <Document className="w-[18px] h-[18px] shrink-0 text-brand-neutral-800" />
  )

  return (
    <div
      className={clsx(
        'text-xs rounded flex items-center px-2 py-1 gap-1.5 min-h-[26px]',
        isError
          ? 'bg-red-100 ring-1 ring-inset ring-red-200'
          : isEmphasized
          ? 'bg-brand-neutral-300'
          : 'bg-brand-neutral-50 ring-1 ring-inset ring-brand-neutral-200',
        // Only show the interactive hover state when the chip is actually viewable.
        canView && (isEmphasized ? 'hocus:bg-brand-neutral-400' : 'hocus:bg-brand-neutral-100')
      )}
    >
      <button
        type="button"
        disabled={!canView}
        onClick={onView}
        className={clsx('flex items-center gap-1.5 min-w-0 flex-1 text-left', canView ? 'cursor-pointer' : 'cursor-default')}
      >
        {icon}
        <Tooltip title={fileName + (errorMessage ? ` (${errorMessage})` : '')} placement="top">
          <span
            className={clsx(
              'flex-1 min-w-0 truncate whitespace-nowrap overflow-hidden font-medium text-wrap line-clamp-1 break-all',
              isError ? 'text-red-950' : 'text-brand-neutral-800'
            )}
          >
            {fileName}
          </span>
        </Tooltip>
      </button>
      <button type="button" className="self-center" onClick={onRemove}>
        <XClear className={clsx('w-[18px] h-[18px] cursor-pointer', isError ? 'text-red-950' : 'text-brand-neutral-600')} />
      </button>
    </div>
  )
}
